/**
 * The account-deletion card: schedule the deletion (with the refusal the
 * server gives while clients or a paid subscription remain), cancel one that
 * is scheduled, or retry reading the schedule. Shared by the profile settings
 * page and the coach account settings page.
 *
 * Aliases: close account, deactivate account card, account deletion card
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { DeactivateAccountModal } from "~/components/deactivate-account-modal";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  userDeletionScheduleQueryOptions,
  useCancelUserDeletion,
  useScheduleUserDeletion,
} from "~/lib/queries/userQuery";
import { USER_DELETION_GRACE_PERIOD_DAYS } from "~/lib/shared/user-deletion";

/**
 * Spells the date out for a warning toast, or gives back null when there is
 * nothing trustworthy to show — the sentence then drops the date rather than
 * printing "Invalid Date" next to an account deletion.
 */
function formatDeletionDate(isoDate: string | undefined): string | null {
  if (!isoDate) {
    return null;
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function CloseAccountCard({
  userId,
  title = "Account deactivation",
}: {
  /** The Clerk user id, which keys the schedule query. */
  userId: string;
  title?: string;
}): React.JSX.Element {
  // Fetched in the browser only, not in a loader: the date is formatted in
  // the viewer's time zone, and a failed read must not take down the page.
  const deletionScheduleQuery = useQuery(
    userDeletionScheduleQueryOptions(userId),
  );
  const scheduledDeletionDate = formatDeletionDate(
    deletionScheduleQuery.data?.scheduledForDeletionAt ?? undefined,
  );
  // Until the schedule is known, offer neither action: showing "Deactivate
  // account" to someone who came to cancel would invite rescheduling instead.
  const deletionCardState = deletionScheduleQuery.isError
    ? "error"
    : deletionScheduleQuery.isPending
      ? "loading"
      : scheduledDeletionDate
        ? "scheduled"
        : "none";

  const [isModalOpen, setIsModalOpen] = useState(false);

  const scheduleUserDeletionMutation = useScheduleUserDeletion({
    onSuccess: (result) => {
      // A refusal stays in the modal, beside the way to settle it.
      if (result.refusal) {
        return;
      }
      // `unsupported` arrives with `success: true` when the Workflows binding
      // is missing, and this attempt scheduled nothing — checking `success`
      // first would promise a deletion that was never recorded.
      if (result.unsupported && result.existingDeletionPending) {
        // A deletion requested earlier survived this attempt untouched. Telling
        // the user nothing was scheduled would read as "deletion is off" while
        // the original job is still counting down to it.
        const deletionDate = formatDeletionDate(result.existingDeletionAt);
        toast.warning(
          deletionDate
            ? `We couldn't reach the deletion service, so nothing changed — your account is still scheduled for deletion on ${deletionDate}. Contact support if you want to keep it.`
            : "We couldn't reach the deletion service, so nothing changed — your account is still scheduled for deletion. Contact support if you want to keep it.",
        );
      } else if (result.unsupported) {
        toast.warning(
          "Your account was not scheduled for deletion — that service is unavailable right now. Please try again later or contact support.",
        );
      } else if (result.success) {
        toast.success(
          `Account deletion scheduled! Your data will be removed in ${USER_DELETION_GRACE_PERIOD_DAYS} days.`,
        );
      } else {
        toast.error(result.error || "Failed to schedule account deletion.");
      }
    },
    onError: () => {
      toast.error("An unexpected error occurred while scheduling deletion.");
    },
  });

  const cancelUserDeletionMutation = useCancelUserDeletion({
    onSuccess: (result) => {
      // As with scheduling, `unsupported` arrives with `success: true` while
      // nothing was cancelled: the deletion is still pending.
      if (result.unsupported) {
        const deletionDate = formatDeletionDate(result.existingDeletionAt);
        toast.warning(
          deletionDate
            ? `We couldn't reach the deletion service, so your account is still scheduled for deletion on ${deletionDate}. Please try again later or contact support.`
            : "We couldn't reach the deletion service, so your account is still scheduled for deletion. Please try again later or contact support.",
        );
      } else if (result.success) {
        toast.success("Your account will not be deleted.");
      } else {
        toast.error(result.error || "Failed to cancel account deletion.");
      }
    },
    onError: () => {
      toast.error("An unexpected error occurred while cancelling deletion.");
    },
  });

  const handleClose = () => {
    setIsModalOpen(false);
    // Opening it again asks again, rather than repeating an old refusal.
    scheduleUserDeletionMutation.reset();
  };

  const handleConfirm = async () => {
    try {
      // The server derives the account from the session; nothing to pass.
      const result = await scheduleUserDeletionMutation.mutateAsync();
      if (!result.refusal) {
        setIsModalOpen(false);
      }
    } catch (error) {
      // Error handling is done in the mutation's onError callback
      console.error("Failed to schedule account deletion:", error);
    }
  };

  return (
    <>
      <Card className="flex-row items-center justify-between border-red-100 bg-red-50/30">
        <div className="flex flex-col gap-1">
          <h2 className="font-heading text-lg font-medium text-gray-900">
            {title}
          </h2>
          <p className="text-text-secondary text-sm">
            {deletionCardState === "error"
              ? "We couldn't check whether your account is scheduled for deletion."
              : deletionCardState === "loading"
                ? "Checking your account status…"
                : deletionCardState === "scheduled"
                  ? `Your account and all its data will be permanently deleted on ${scheduledDeletionDate}.`
                  : "Permanently deactivate your account and delete all data"}
          </p>
        </div>
        {deletionCardState === "error" && (
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => void deletionScheduleQuery.refetch()}
          >
            Try again
          </Button>
        )}
        {deletionCardState === "scheduled" && (
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => cancelUserDeletionMutation.mutate()}
            loading={cancelUserDeletionMutation.isPending}
          >
            Keep my account
          </Button>
        )}
        {deletionCardState === "none" && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="shrink-0 bg-red-50 text-red-600 hover:bg-red-100"
            onClick={() => setIsModalOpen(true)}
          >
            Deactivate account
          </Button>
        )}
      </Card>

      <DeactivateAccountModal
        isOpen={isModalOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isLoading={scheduleUserDeletionMutation.isPending}
        refusal={scheduleUserDeletionMutation.data?.refusal}
      />
    </>
  );
}
